import React from 'react';
import { addSeconds } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import Modal from 'components/ui/Modal';
import useInactivity from 'hooks/useInactivity';
import { useDispatch, useSelector } from 'hooks';
import Button, { ButtonType } from 'components/ui/Button';
import { ScheduleTypeEnum } from 'modules/auth/types/InitialState';
import { INACTIVITY_TIME_TO_LOGOUT } from 'modules/auth/constants';
import { logout, resetSchedule, setSchedule } from 'modules/auth/actions';
import { getScheduledTime, hasScheduledTime } from 'modules/auth/selectors';
import InactivityProgressBar from 'components/Admin/InactivityModal/InactivityProgressBar';

const Inactivity: React.FC<{ seconds: number, paused?: boolean }> = ({ seconds, paused }) => {
  const dispatch = useDispatch();

  const inactivityDate = useSelector((state) => getScheduledTime(state, ScheduleTypeEnum.LOGOUT));
  const displayInactivityModal = useSelector((state) => hasScheduledTime(state, ScheduleTypeEnum.LOGOUT));

  const shouldShowModal = !paused && displayInactivityModal;

  const handleShowInactivityModal = React.useCallback(() => {
    dispatch(
      setSchedule({
        [ScheduleTypeEnum.LOGOUT]: addSeconds(Date.now(), INACTIVITY_TIME_TO_LOGOUT).toISOString(),
      }),
    );
  }, [dispatch]);

  const { restart } = useInactivity({
    paused,
    seconds,
    onInactive: handleShowInactivityModal,
  });

  const handleContinue = React.useCallback(() => {
    dispatch(resetSchedule());
    restart();
  }, [dispatch, restart]);

  const handleLogout = React.useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  React.useEffect(() => {
    if (!paused) {
      restart();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [seconds, paused]);

  return (
    shouldShowModal && (
      <Modal
        role="alertdialog"
        open
        className="modal-large"
        title={(
          <div className="mb-16">
            <span className="text-center">
              <FormattedMessage id="inactivity.modal.title" defaultMessage="Are you still there?" tagName="h2" />
            </span>
          </div>
        )}
      >
        <div className="px-[20%]">
          <InactivityProgressBar date={inactivityDate} onEnded={handleLogout} />
        </div>
        <div className="flex items-center gap-4 mt-20">
          <Button testId="modal-continue" onClick={handleContinue} className="w-1/2">
            <FormattedMessage id="continue.yes" defaultMessage="Yes, continue" />
          </Button>
          <Button testId="modal-confirm" onClick={handleLogout} className="w-1/2" type={ButtonType.SECONDARY}>
            <FormattedMessage id="socket.updateLogoutModal.logOut" defaultMessage="Log out" values={{ countdown: 0 }} />
          </Button>
        </div>
      </Modal>
    )
  );
};

export default Inactivity;
